import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Banner,
  Card,
  EmptyState,
  FilterChips,
  StatusPill,
  Table,
  TCell,
  THead,
  TRow,
} from '@cou/admin-ui';
import {
  defaultEntriesClient,
  ENTRY_STATES,
  type AdminEntryRow,
  type EntriesClient,
} from '../entries/entries-client';
import { useAdminAuth } from '../session/admin-auth-context';
import { entryPillState } from '../entries/entry-state';
import { formatDateParts } from '../contests/format-date';

/**
 * The contest detail's Entries tab (API-22, admin-ui.md → Contest detail › Entries): who entered,
 * under which Segment, and how they scored — click through to the Picks.
 *
 * Read-only. Every Entry mutation (recompute, void) is settlement tooling behind `contests:settle`
 * (API-11), so this tab gates on `users:view` alone and offers no action.
 */

export interface ContestEntriesTabProps {
  readonly contestId: string;
  /** Injected for tests; defaults to the real API-22 client. */
  readonly client?: EntriesClient;
}

const STATE_CHIPS = [
  { key: 'all', label: 'All' },
  ...ENTRY_STATES.map((state) => ({ key: state, label: state })),
];

export function ContestEntriesTab({
  contestId,
  client = defaultEntriesClient,
}: ContestEntriesTabProps) {
  const navigate = useNavigate();
  const { getToken } = useAdminAuth();
  const [status, setStatus] = useState('all');
  const [entries, setEntries] = useState<AdminEntryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const token = getToken();
    if (token === null) return;
    const result = await client.listEntriesForContest(
      token,
      contestId,
      status === 'all' ? {} : { status },
    );
    if (result.ok) {
      setEntries(result.entries);
      setError(null);
    } else {
      // An error must not read as "no entries yet" — that is the bug CAE-357 describes from the
      // other direction, and it is worse when the contest genuinely has entrants.
      setError(result.message ?? 'The entries lookup failed.');
      setEntries(null);
    }
  }, [client, contestId, getToken, status]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Card title="Entries">
      <FilterChips chips={STATE_CHIPS} active={status} onSelect={setStatus} />

      {error !== null && (
        <Banner data-testid="contest-entries-error" variant="danger" className="mt-3">
          {error}
        </Banner>
      )}

      {entries !== null && entries.length === 0 && (
        <EmptyState
          data-testid="contest-entries-empty"
          variant="section"
          icon={<EntriesIcon />}
          title="No entries yet"
          body="Entries appear once the contest opens and players submit picks."
        />
      )}

      {entries !== null && entries.length > 0 && (
        <Table className="mt-3" columns="minmax(0,1.4fr) 140px 120px 110px 160px">
          <THead>
            <TCell>Account ref</TCell>
            <TCell>Segment</TCell>
            <TCell>State</TCell>
            <TCell>Correct</TCell>
            <TCell>Submitted</TCell>
          </THead>
          {entries.map((entry) => (
            <TRow
              key={entry.id}
              data-testid={`contest-entry-${entry.id}`}
              onClick={() => void navigate(`/entries/${entry.id}`)}
              className="cursor-pointer"
            >
              <TCell variant="mono">{entry.account}</TCell>
              {/* The Segment snapshotted at submission — the payout-matrix column this Entry is
                  graded against, not the player's segment today. Read by its registry label, as the
                  entry detail and Awards queue show it; the raw ref stands in when unlabelled so the
                  surfaces reconcile (CAE-559). */}
              <TCell>{entry.segmentLabel ?? entry.segmentSnapshot ?? 'default'}</TCell>
              <TCell>
                <StatusPill kind="entry" state={entryPillState(entry.status)} />
              </TCell>
              <TCell variant="num">{entry.correctPicks}</TCell>
              <TCell variant="mono">
                {entry.submittedAt === null ? '—' : formatDateParts(entry.submittedAt).date}
              </TCell>
            </TRow>
          ))}
        </Table>
      )}
    </Card>
  );
}

function EntriesIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 7h16M4 12h16M4 17h10"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}
